import type { ReactNode } from "react";
import { ShieldAlert } from "lucide-react";
import type { Me } from "../api/auth.js";

/** What an admin policy demands before the vault opens again. */
export type PolicyGate = "two-factor" | "password-length";

/**
 * The open policy demand for this account, if any. Until it is met, only the settings page is
 * reachable; the server keeps vault and teams closed for the 2FA demand as well.
 */
export function policyGate(me: Me | undefined, passwordLength: number | undefined): PolicyGate | undefined {
  if (me === undefined) {
    return undefined;
  }
  if (me.twoFactorSetupRequired) {
    return "two-factor";
  }
  if (passwordLength !== undefined && passwordLength < me.policies.minPasswordLength) {
    return "password-length";
  }
  return undefined;
}

const TEXT: Record<PolicyGate, { readonly title: string; readonly body: string }> = {
  "two-factor": {
    title: "Zwei-Faktor-Anmeldung erforderlich",
    body: "Dein Administrator verlangt einen zweiten Faktor. Richte unten eine Authenticator-App oder einen Passkey ein – danach sind Tresor und Teams wieder erreichbar."
  },
  "password-length": {
    title: "Neues Master-Passwort erforderlich",
    body: "Dein Master-Passwort ist kürzer, als dein Administrator verlangt. Ändere es unten – danach ist dein Tresor wieder erreichbar."
  }
};

export function PolicyBanner(props: { readonly gate: PolicyGate }): ReactNode {
  const text = TEXT[props.gate];
  return (
    <div role="alert" className="border-b border-buoy/30 bg-buoy/10 px-4 py-3 sm:px-6">
      <div className="mx-auto flex max-w-3xl items-start gap-3">
        <ShieldAlert size={18} aria-hidden="true" className="mt-0.5 shrink-0 text-buoy" />
        <div className="text-sm leading-relaxed">
          <p className="font-semibold text-foam">{text.title}</p>
          <p className="text-mist">{text.body}</p>
        </div>
      </div>
    </div>
  );
}
